import React, { FC } from 'react';
import styled from '@emotion/styled';
import { BlueButton, Box, Button } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useI18n, useSelector } from '~Hooks';
import { useHistory } from 'react-router-dom';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import { keyframes } from '@emotion/react';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Wrapper = styled(Button)<{
  backgroundImageSrc?: string;
  animationDelay?: number;
  isInitialRender?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border: 1px solid var(--colorBlueGray1000);
  border-radius: 8px;
  padding: 16px;
  text-transform: none;
  min-height: 64px;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  /* Endast animera vid initial render */
  opacity: ${(props) => (props.isInitialRender ? 0 : 1)};
  ${(props) =>
    props.isInitialRender
      ? `
        animation: ${fadeInUp} 0.4s ease-out forwards;
        animation-delay: ${props.animationDelay || 0}ms;
      `
      : `
        animation: none;
      `}

  ${(props) =>
    props.backgroundImageSrc &&
    `
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 50%;
      background-image: url(${props.backgroundImageSrc});
      background-size: cover;
      background-position: center right;
      background-repeat: no-repeat;
      opacity: 0.15;
      pointer-events: none;
      mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
      -webkit-mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    }
  `}
`;

const CancelButton = styled(Button)`
  max-width: 160px;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  isClaimable: boolean;
  text: string;
  to: string;
  iconSrc: string;
  backgroundImageSrc?: string;
  animationDelay?: number;
}

const Mode: FC<Props> = ({
  isClaimable,
  text,
  to,
  iconSrc,
  backgroundImageSrc,
  animationDelay,
}) => {
  const { i18n } = useI18n();
  const history = useHistory();
  const modal = useModal();

  const venueId = useSelector((state) => state.venueId);

  const isExpress = to.includes('self-service-kiosks');

  const cancelButton = useButton({
    onClick: () => {
      modal.hide();
    },
  });

  const continueButton = useButton({
    onClick: () => {
      history.push(to);
    },
  });

  const handleClick = () => {
    if (isExpress) {
      modal.show();
    }
  };

  const modeButton = useButton({
    onClick: () => {
      if (isExpress) {
        modal.show();
        return;
      }

      history.push(to);
    },
  });

  return (
    <>
      <Wrapper
        fluid
        {...modeButton}
        backgroundImageSrc={backgroundImageSrc}
        animationDelay={animationDelay}
        isInitialRender={!venueId}>
        <Box
          display="flex"
          gap="small"
          alignItems="center"
          style={{ position: 'relative', zIndex: 1 }}>
          <IconWrapper>
            <Icon height="32px" width="32px" src={iconSrc} color="var(--colorBlueGray200)" />
          </IconWrapper>
          <Heading level={3} color="var(--colorBlueGray200)" bottom={0}>
            {text}
          </Heading>
        </Box>
      </Wrapper>

      <Modal isShowing={modal.isShowing} hide={modal.toggle} backdropClose>
        <ModalWrapper
          padding="3xl"
          header={{
            title: i18n('EnterSelfServiceKiosk.Title'),
            subtitle: i18n('EnterSelfServiceKiosk.Desc'),
            icon: iconSrc,
          }}>
          <Box
            display="flex"
            flexDirection="row"
            gap="xs"
            marginTop="xs"
            alignItems="center"
            fluid>
            <CancelButton fluid {...cancelButton}>
              {i18n('General.Cancel')}
            </CancelButton>

            <BlueButton fluid {...continueButton}>
              {i18n('App.Continue')}
            </BlueButton>
          </Box>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default Mode;
